import {useEffect} from 'react';
import {useParams} from 'react-router-dom';

import {openPapasWebview} from '@zzng/common/scheme/digitaldocs';
import {appendQueryParamToUrl} from '@zzng/common/utils/common';
import {openLandingUrl} from '@zzng/common/utils/openLandingUrl';

import {ROUTES} from '@/root/constants/routes';
import {useHomeTiara} from '@/root/hooks/useHomeTiara';
import {useCreatePapasTransaction, useRecommendCardIntro} from '@/root/queries/recommend';
import {checkValidSchemeUrl} from '@/root/utils/checkValidSchemeUrl';
import {getPapasErrorCode, isRecommendCodeUsingPapas} from '@/root/utils/recommendCode';

import CardInfoFooter from '@/shared/components/CardInfo/Footer';
import Halfview from '@/shared/components/Halfview';
import Loading from '@/shared/components/Loading';
import LoadingButton from '@/shared/components/LoadingButton';
import {TIARA_HOME_ACTION} from '@/shared/constants/tiara';
import withSuspense from '@/shared/hoc/withSuspense';
import useAdvancedNavigate from '@/shared/hooks/useAdvancedNavigate';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import {useHalfviewStore} from '@/shared/stores/halfview';
import {sendTMS} from '@/shared/utils/developers';
import {nl2br} from '@/shared/utils/nl2br';

import {RecommendCodeUsingPapas} from '@/apps/root/constants/recommendCode';
import usePopup from '@/apps/root/hooks/usePopup';

const TIARA_PARAMS = {
  t_src: 'kakaotalk',
  t_ch: 'digitalcardhome',
  t_obj: 'recommendcardissue'
};

const RecommendCardIntroPage = () => {
  const {recommendCode = ''} = useParams<{recommendCode: string}>();
  const navigate = useAdvancedNavigate();
  const {classnames, setClassnames, closeHalfview} = useHalfviewStore();
  const {trackEvent} = useHomeTiara({pageView: TIARA_HOME_ACTION.ISSUE_VIEW});
  const {cardIntro} = useRecommendCardIntro(recommendCode);
  const {createPapasTransactionMutation} = useCreatePapasTransaction();
  const {openPopup} = usePopup();

  const onClickShare = () => {
    trackEvent(TIARA_HOME_ACTION.SHARE_CLICK);

    sendTMS('DICARON_CARD', {
      INTRO_URL: window.location.href,
      IMAGE_URL: cardIntro.tmsImageUrl,
      CARD_TITLE: cardIntro.title
    });
  };

  const onExited = () => {
    navigate(ROUTES.HOME, {keepSearchParams: true});
  };

  const onClickIssue = () => {
    trackEvent(TIARA_HOME_ACTION.ISSUE_CLICK);
    checkValidSchemeUrl(cardIntro.issueUrl);

    if (!isRecommendCodeUsingPapas(recommendCode)) {
      const issueUrl = appendQueryParamToUrl(cardIntro.issueUrl, TIARA_PARAMS);
      openLandingUrl(issueUrl);
      closeHalfview();
      return;
    }

    // 파파스 플로우 발급 시 트랜잭션 생성 후 발급 URL로 이동
    createPapasTransactionMutation.mutate(recommendCode, {
      onSuccess: ({txId, papasUrl}, code: RecommendCodeUsingPapas) => {
        openPapasWebview({
          isCurrentWebviewClose: false,
          papasUrl,
          papasTxId: txId,
          papasReturnUrl: {
            url: `${location.origin}/issue/papas/progress`,
            params: {
              cardType: code,
              ...TIARA_PARAMS
            }
          }
        });
        closeHalfview();
      },
      onError: (error) => {
        const errorCode = getPapasErrorCode(error);
        openPopup('PAPAS_ERROR', {errorCode});
      }
    });
  };

  useHistoryBackAOS(() => {
    closeHalfview();
  }, []);

  useEffect(() => {
    setClassnames('modal_card');
  }, []);

  return (
    <Halfview.Wrapper classnames={classnames} onExited={onExited}>
      <Halfview.Header>
        <Halfview.Title headingLevel="h4">{cardIntro.title}</Halfview.Title>
        {cardIntro.isShareable && (
          <Halfview.HeaderButton className="btn_share" onClick={onClickShare}>
            <span className="icon icon_share">공유하기</span>
          </Halfview.HeaderButton>
        )}
      </Halfview.Header>
      <Halfview.Body>
        <div className="inner_g">
          <span className="thumb_card" style={{backgroundImage: `url(${cardIntro.imageUrl})`}}></span>
          <strong className="tit_card" role="text">
            {nl2br(cardIntro.intro)}
          </strong>
          <div className="group_box">
            <CardInfoFooter descriptions={cardIntro.descriptions} />
          </div>
        </div>
      </Halfview.Body>
      <Halfview.Footer>
        <div className="group_btn">
          {cardIntro.isMaintenance ? (
            <button type="button" className="btn disabled" disabled onClick={(e) => e.preventDefault()}>
              점검
            </button>
          ) : (
            <LoadingButton
              isLoading={createPapasTransactionMutation.isPending}
              text="발급 받기"
              onClick={onClickIssue}
            />
          )}
        </div>
      </Halfview.Footer>
    </Halfview.Wrapper>
  );
};

export default withSuspense(RecommendCardIntroPage, Loading);
